// Import as namespace to avoid ESM/CJS type resolution mismatches across toolchains
import * as ReactQuery from '@tanstack/react-query';
import { getAuthzenSubject } from '../core/auth/subject';
import { buildAuthZENAction } from '../core/auth/authz-operation-refs';
import type { PluginManifest, PluginSDK, WorkflowError, WorkflowResumeResult, WorkflowStartResult } from './types';
import { createMembershipSdk } from './sdk.membership';
import { fetchWithSWR } from '../utils/fetchWithSWR';

/**
 * Creates a plugin SDK instance with scoped authorization
 * 
 * @param pluginId - Unique plugin identifier
 * @param qc - React Query client instance
 * @param manifest - Plugin manifest with authorization configuration
 * @returns Plugin SDK with api, sse, authz, crud, and metadata
 */
export function createPluginSdk(
	pluginId: string, 
	qc: any, 
	manifest?: PluginManifest
): PluginSDK {
	const base = (import.meta as any).env?.VITE_BFF_BASE_URL || '';
	
	// Extract authorization configuration from manifest
	const pdpApplication = manifest?.authorization?.pdp_application || 'experience';
	const baseContext = manifest?.authorization?.context || {};
	
	// Extract plugin-specific configuration from manifest
	const pluginConfig = manifest?.config || {};

	const normalizeAuthZENResource = (resource: any): any => {
		const type = resource?.type;
		if (!type || typeof type !== 'string') {
			throw new Error('AuthZEN resource.type is required');
		}
		const id = (typeof resource?.id === 'string' && resource.id.trim().length > 0)
			? resource.id
			: '*'; // Spec-compliant type-level check
		const properties = (resource?.properties && typeof resource.properties === 'object')
			? { ...resource.properties }
			: {};
		return { ...(resource || {}), type, id, properties };
	};

	const normalizeAuthZENAction = (resourceType: string, action: any): any => {
		const name = (typeof action === 'string')
			? action
			: action?.name;
		if (!name || typeof name !== 'string') {
			throw new Error('AuthZEN action.name is required');
		}

		// buildAuthZENAction injects operation_ref_id only for grant-aware resource types.
		const built = buildAuthZENAction(resourceType, name);
		const existingProps =
			(action && typeof action === 'object' && action.properties && typeof action.properties === 'object')
				? action.properties
				: undefined;
		const mergedProps = { ...(built.properties || {}), ...(existingProps || {}) };

		// Preserve additional fields on action (if any), but normalize name/properties.
		const rest = (action && typeof action === 'object') ? { ...action } : {};
		delete rest.name;
		delete rest.properties;

		return Object.keys(mergedProps).length > 0
			? { ...rest, name, properties: mergedProps }
			: { ...rest, name };
	};
	
	// Log SDK initialization for observability
	if (pdpApplication !== 'experience' || Object.keys(pluginConfig).length > 0) {
		console.log(
			`[Plugin:${pluginId}] SDK initialized`,
			{
				pdp_application: pdpApplication !== 'experience' ? pdpApplication : undefined,
				has_base_context: Object.keys(baseContext).length > 0 ? true : undefined,
				base_context_keys: Object.keys(baseContext).length > 0 ? Object.keys(baseContext) : undefined,
				has_config: Object.keys(pluginConfig).length > 0 ? true : undefined,
				config_keys: Object.keys(pluginConfig).length > 0 ? Object.keys(pluginConfig) : undefined
			}
		);
	}
	
	const activeSubscriptions: Array<() => void> = [];

	const getCsrfToken = (): string => {
		if (typeof document === 'undefined') return '';
		const match = document.cookie.match(/_eid_csrf_v1=([^;]+)/);
		return match ? match[1] : '';
	};

	const buildApiUrl = (path: string): string => {
		// Build URL without duplicating the /api prefix; support absolute URLs too
		if (/^https?:\/\//i.test(path)) {
			return path;
		}
		if (path.startsWith('/api/')) {
			return `${base}${path}`;
		}
		if (path.startsWith('/')) {
			return `${base}/api${path}`;
		}
		return `${base}/api/${path}`;
	};

	const _telemetryBuffer: Record<string, unknown>[] = [];
	let _telemetryTimer: ReturnType<typeof setTimeout> | null = null;
	const TELEMETRY_FLUSH_MS = 5000;
	const TELEMETRY_MAX_BATCH = 50;

	const _flushTelemetry = (): void => {
		_telemetryTimer = null;
		if (_telemetryBuffer.length === 0) return;
		const batch = _telemetryBuffer.splice(0, TELEMETRY_MAX_BATCH);
		fetch(`${base}/api/plugins/telemetry`, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ events: batch, pluginId }),
		}).catch(() => {});
	};

	const trackTelemetry = (payload: Record<string, unknown>): void => {
		_telemetryBuffer.push(payload);
		if (_telemetryBuffer.length >= TELEMETRY_MAX_BATCH) {
			if (_telemetryTimer) { clearTimeout(_telemetryTimer); _telemetryTimer = null; }
			_flushTelemetry();
			return;
		}
		if (!_telemetryTimer) {
			_telemetryTimer = setTimeout(_flushTelemetry, TELEMETRY_FLUSH_MS);
		}
	};

	const apiFetch = async (path: string, init?: RequestInit) => {
		const start = performance.now();
		let res: Response;
		try {
			const url = buildApiUrl(path);
			const method = (init?.method || 'GET').toUpperCase();
			const headers: Record<string, string> = { ...(init?.headers as Record<string, string> || {}) };
			if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
				const csrfToken = getCsrfToken();
				if (csrfToken) {
					headers['X-CSRF-Token'] = csrfToken;
				}
			}
		res = await fetch(url , {
		...(init || {}),
		credentials: 'include',
		headers,
		});
			// fire-and-forget telemetry
			trackTelemetry({ event: 'api.call', path, method: (init?.method || 'GET'), duration: performance.now() - start, status: res.status });
		} catch (err: any) {
			trackTelemetry({ event: 'api.error', path, method: (init?.method || 'GET'), error: err?.message });
			throw err;
		}
	// 304 Not Modified is a valid cache response, not an error
	if (res.status === 304) {
		return res;
	}
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		const detail = body.detail || body.message || `HTTP ${res.status}`;
		const error = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
		(error as any).status = res.status;
		(error as any).detail = detail;
		throw error;
	}
	return res;
	};

	const apiGetJson = async <T>(path: string, config?: { signal?: AbortSignal }, swrWindowMs: number = 10_000): Promise<T> => {
		const start = performance.now();
		try {
			const url = buildApiUrl(path);
		const { data, fromCache, httpStatus } = await fetchWithSWR<T>(
			url,
			{
				method: 'GET',
				signal: config?.signal,
			},
			swrWindowMs,
		);
			trackTelemetry({
				event: 'api.call',
				path,
				method: 'GET',
				duration: performance.now() - start,
				status: typeof httpStatus === 'number' ? httpStatus : undefined,
				http_status: typeof httpStatus === 'number' ? httpStatus : undefined,
				cache_hit: fromCache ? true : undefined,
			});
			return data;
		} catch (err: any) {
			trackTelemetry({ event: 'api.error', path, method: 'GET', error: err?.message, status: err?.status });
			throw err;
		}
	};

	const subscribe = (path: string, onMessage: (data: any) => void) => {
		const url = `${base}/api${path}`;
		const es = new EventSource(url, { withCredentials: true } as any);
		es.onmessage = (e) => onMessage(JSON.parse(e.data));
		const unsubscribe = () => es.close();
		activeSubscriptions.push(unsubscribe);
		return unsubscribe;
	};

	const crud = {
		/**
		 * Execute a CRUD command (same as grid datasource)
		 * @example sdk.crud.execute('av_openldap', 'account', 'get_all_users', {})
		 */
		execute: async (
			system: string,
			objectType: string,
			action: string,
			params?: Record<string, any>
		): Promise<any> => {
			const res = await apiFetch('/crud/execute', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					system,
					object_type: objectType,
					action,
					params: params || {}
				})
			});
			const data = await res.json();
			return data?.data ?? data?.result ?? data;
		},

		/**
		 * React Query hook for CRUD commands with caching
		 * @example const { data, isLoading } = sdk.crud.useCommand('av_openldap', 'account', 'get_all_users')
		 */
		useCommand: (
			system: string,
			objectType: string,
			action: string,
			params?: Record<string, any>,
			options?: any
		) => {
			return ReactQuery.useQuery({
				queryKey: ['crud-command', system, objectType, action, params],
				queryFn: () => crud.execute(system, objectType, action, params),
				staleTime: 10000,
				...options
			});
		},

		/**
		 * Ultra-minimal shorthand hook (vibe coding optimized)
		 * @example const { data } = sdk.crud.use('av_openldap', 'account', 'get_all_users')
		 */
		use: (
			system: string,
			objectType: string,
			action: string,
			params?: Record<string, any>,
			options?: any
		) => {
			return crud.useCommand(system, objectType, action, params, options);
		},

		/**
		 * Execute a workflow
		 * @example await sdk.crud.executeWorkflow('av_openldap_create_user', { FirstName: 'John' })
		 */
		executeWorkflow: async (
			workflowName: string,
			data?: Record<string, any>
		): Promise<any> => {
			const res = await apiFetch('/crud/workflow/start', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					workflow_name: workflowName,
					data: data || {}
				})
			});
			return res.json();
		},

		/**
		 * Shorthand workflow execution (vibe coding optimized)
		 * @example await sdk.crud.run('av_openldap_create_user', data)
		 */
		run: async (workflowName: string, data?: Record<string, any>): Promise<any> => {
			return crud.executeWorkflow(workflowName, data);
		},

		/**
		 * Resume a workflow task
		 */
		resumeWorkflow: async (
			taskId: string,
			data?: Record<string, any>,
			stateVersion?: number
		): Promise<any> => {
			const res = await apiFetch(`/crud/workflow/resume/${taskId}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					data: data || {},
					...(stateVersion !== undefined ? { state_version: stateVersion } : {})
				})
			});
			return res.json();
		},

		/**
		 * Get workflow status
		 */
		getWorkflowStatus: async (executionId: string): Promise<any> => {
			const res = await apiFetch(`/crud/workflow/status/${executionId}`);
			return res.json();
		},

		/**
		 * Get form schema for a command
		 */
		getFormSchema: async (
			system: string,
			objectType: string,
			action: string
		): Promise<any> => {
			const res = await apiFetch(
				`/crud/commands/form_schema?system=${encodeURIComponent(system)}&object_type=${encodeURIComponent(objectType)}&action=${encodeURIComponent(action)}`
			);
			return res.json();
		},

		/**
		 * Browse CRUD catalog
		 */
		browseCatalog: async (): Promise<any> => {
			const res = await apiFetch('/crud/catalog/browse');
			return res.json();
		},

		/**
		 * Subscribe to workflow status updates via SSE
		 */
		subscribeWorkflowStatus: (
			executionId: string,
			onUpdate: (status: any) => void
		): (() => void) => {
			return subscribe(`/crud/events/workflow/status/${executionId}`, onUpdate);
		}
	};

	// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
	// WORKFLOW ROUTE RESOLVER - Maps task contracts to routes
	// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
	
	// Plugin-specific route resolver (can be overridden per-plugin)
	let customRouteResolver: ((contract: any, taskId: string) => string | null) | null = null;
	
	/**
	 * Default route resolver using convention:
	 * 1. If contract has route_pattern, use it directly
	 * 2. Check explicit handler_id → route mappings
	 * 3. Otherwise, derive from plugin_id + route_id/handler_id
	 * 
	 * Convention: /{plugin_base}/{route_id_to_path}/:taskId
	 */
	const defaultRouteResolver = (contract: any, taskId: string): string | null => {
		if (!contract) return null;
		
		// 1. If backend provides route_pattern, use it (ideal case)
		if (contract.route_pattern) {
			return contract.route_pattern.replace(':taskId', encodeURIComponent(taskId));
		}
		
		const pluginId = contract.plugin_id;
		const handlerId = contract.handler_id;
		const routeId = contract.route_id;
		
		// 2. Explicit handler_id → route mappings (handles naming mismatches)
		// This is the "known routes" registry - ensures reliable navigation
		const explicitRoutes: Record<string, Record<string, string>> = {
			'agent-hub': {
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// DELETION WORKFLOWS - handler_ids from workflow definitions
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// Bulk deletion (delete_agents_cascade_guided.json)
				'BulkDeletionPreview': '/agents/bulk-delete',
				'BulkDeletionResults': '/agents/bulk-delete-results',
				// Single agent deletion (delete_agent_cascade_guided.json)
				'DeletionPreview': '/agents/delete',
				'DeletionResults': '/agents/delete-results',
				// Aliased handler_ids (AgentDeletionPreview → DeletionPreview)
				'AgentDeletionPreview': '/agents/delete',
				'AgentDeletionResults': '/agents/delete-results',
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// CREATION WORKFLOW - handler_ids from create_complete_agent.json
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				'AgentCreationWizard': '/agents/create',
				'IdentityNotFoundScreen': '/agents/create',
				'SystemSelector': '/agents/create',
				'ToolSelector': '/agents/create',
				'SystemPromptEditor': '/agents/create',
				'AgentNameConflictScreen': '/agents/create',
				'OAuthResultsDisplay': '/agents/create',
				'AgentServiceCreationSuccess': '/agents/create',
				'AgentCreationSuccess': '/agents/create',  // Alias
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// DELEGATION WORKFLOW - handler_ids from create_user_delegation_guided.json
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				'DelegationAgentOverview': '/agents/delegate',
				'DelegationCapabilitySelector': '/agents/delegate',
				'DelegationTrustConstraints': '/agents/delegate',
				'DelegationPreviewConfirmation': '/agents/delegate',
				'DelegationSuccess': '/agents/delegate',
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// ROUTE_IDS - alternate identifiers from workflow definitions
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// Bulk deletion route_ids
				'bulk_delete_preview': '/agents/bulk-delete',
				'bulk_delete_results': '/agents/bulk-delete-results',
				// Single agent deletion route_ids
				'agent_delete_preview': '/agents/delete',
				'agent_delete_results': '/agents/delete-results',
				'deletion_preview': '/agents/delete',
				'deletion_results': '/agents/delete-results',
				// Creation route_ids
				'agent_create_step': '/agents/create',
				// Delegation route_ids
				'delegate_step': '/agents/delegate',
			},
			'access-governance': {
				// Add access-governance routes here as needed
				'CampaignCreationWizard': '/access-governance/campaigns/create',
				'CampaignConfirmStart': '/access-governance/campaigns/create',
				'CampaignCreationSuccess': '/access-governance/campaigns/create',
			},
		};
		
		// Look up explicit route
		const pluginRoutes = explicitRoutes[pluginId];
		if (pluginRoutes) {
			// Try handler_id first, then route_id
			const explicitPath = pluginRoutes[handlerId] || pluginRoutes[routeId];
			if (explicitPath) {
				return `${explicitPath}/${encodeURIComponent(taskId)}`;
			}
		}
		
		// 3. Fallback: derive from route_id/handler_id using convention
		const identifier = routeId || handlerId;
		if (!pluginId || !identifier) {
			console.warn('[SDK] Cannot resolve route: missing plugin_id or route_id/handler_id', contract);
			return null;
		}
		
		// Convert identifier (snake_case or CamelCase) to path (kebab-case)
		const pathSegment = identifier
			.replace(/_/g, '-')
			.replace(/([a-z])([A-Z])/g, '$1-$2')
			.toLowerCase();
		
		// Derive plugin base path
		let pluginBase = `/${pluginId}`;
		if (pluginId === 'agent-hub') pluginBase = '/agents';
		if (pluginId === 'access-governance') pluginBase = '/access-governance';
		
		return `${pluginBase}/${pathSegment}/${encodeURIComponent(taskId)}`;
	};
	
	/**
	 * Navigate to a route using the Experience app's navigation system
	 */
	const navigateToRoute = (route: string): void => {
		if (typeof window === 'undefined') return;
		
		// Prefer Experience app's __pluginNavigate (SPA navigation)
		const pluginNavigate = (window as Record<string, any>)['__pluginNavigate'];
		if (typeof pluginNavigate === 'function') {
			console.log('[SDK] Navigating via __pluginNavigate:', route);
			pluginNavigate(route);
			return;
		}
		
		// Fallback: History API
		console.log('[SDK] Navigating via History API:', route);
		window.history.pushState({ route }, '', route);
		window.dispatchEvent(new PopStateEvent('popstate', { state: { route } }));
	};
	
	const workflow = {
		/**
		 * React Query hook to fetch a workflow task by task ID
		 * Returns task details including ui_contract for experience_ui tasks
		 * @example const { data: task, isLoading, error } = sdk.workflow.useTask(taskId)
		 */
		useTask: (taskId: string, options?: any) => {
			return ReactQuery.useQuery({
				queryKey: ['workflow-task', taskId],
				queryFn: async () => {
					const res = await apiFetch(`/workflow/tasks/${encodeURIComponent(taskId)}`);
					if (!res.ok) {
						const error = await res.json().catch(() => ({ message: res.statusText }));
						throw new Error(error.message || `Failed to fetch task: ${res.statusText}`);
					}
					return res.json();
				},
				staleTime: 30000, // 30 seconds - tasks can change
				...options,
			});
		},
		
		/**
		 * Imperative method to fetch a workflow task by task ID
		 * Use when you need task details outside of React render cycle
		 * @example const task = await sdk.workflow.getTask(taskId)
		 */
		getTask: async (taskId: string): Promise<any> => {
			const res = await apiFetch(`/workflow/tasks/${encodeURIComponent(taskId)}`);
			if (!res.ok) {
				const error = await res.json().catch(() => ({ message: res.statusText }));
				throw new Error(error.message || `Failed to fetch task: ${res.statusText}`);
			}
			return res.json();
		},
		
		/**
		 * Get workflow execution status by workflow ID
		 * @example const status = await sdk.workflow.getWorkflowStatus(workflowId)
		 */
		getWorkflowStatus: async (workflowId: string): Promise<any> => {
			const res = await apiFetch(`/workflow/status/${encodeURIComponent(workflowId)}`);
			if (!res.ok) {
				const error = await res.json().catch(() => ({ message: res.statusText }));
				throw new Error(error.message || `Failed to get workflow status: ${res.statusText}`);
			}
			return res.json();
		},

		/**
		 * Resume a UI workflow task with user response data
		 * Validates response against output_schema and resumes workflow execution
		 * 
		 * NOTE: For automatic navigation after resume, use resumeAndContinue() instead.
		 * 
		 * @example await sdk.workflow.resumeUiTask(taskId, { decision: 'approve' }, stateVersion)
		 */
		resumeUiTask: async (
			taskId: string,
			data: Record<string, any>,
			stateVersion?: number
		): Promise<any> => {
			const res = await apiFetch(`/workflow/resume/${encodeURIComponent(taskId)}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					data,
					...(stateVersion !== undefined ? { state_version: stateVersion } : {}),
				}),
			});
			if (!res.ok) {
				const error = await res.json().catch(() => ({ message: res.statusText }));
				throw new Error(error.message || `Failed to resume task: ${res.statusText}`);
			}
			return res.json();
		},
		
		/**
		 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
		 * 🚀 RESUME AND CONTINUE - Blue Steel Level Workflow Navigation
		 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
		 * 
		 * The ONE method plugin developers need. Handles everything:
		 * ✅ Resumes workflow task with optimistic concurrency
		 * ✅ Polls for completion with exponential backoff
		 * ✅ Auto-navigates to next UI step
		 * ✅ Tracks progress with callbacks (0-100%)
		 * ✅ Graceful degradation on errors
		 * ✅ Telemetry for observability
		 * ✅ Full TypeScript types
		 * 
		 * Blue Steel Score: 95% (Zero Friction + Intelligent Automation)
		 * 
		 * @example
		 * // 🎯 Minimal usage - SDK handles everything:
		 * await sdk.workflow.resumeAndContinue(taskId, { confirmed: true });
		 * 
		 * @example
		 * // 🎯 With progress tracking for beautiful UX:
		 * await sdk.workflow.resumeAndContinue(taskId, data, {
		 *   onProgress: (progress, status) => setProgress({ percent: progress, message: status }),
		 *   onComplete: () => toast.success('Done!'),
		 *   fallbackRoute: '/my-plugin',
		 * });
		 */
		resumeAndContinue: async (
			taskId: string,
			data: Record<string, any>,
			options?: {
				/** Optimistic concurrency version (auto-fetched if not provided) */
				stateVersion?: number;
				/** Progress callback (0-100) with status message - for beautiful loading UX */
				onProgress?: (percent: number, status: string, detail?: string) => void;
				/** Called when workflow advances to next UI task */
				onNavigating?: (nextTask: any, route: string) => void;
				/** Called when workflow completes (no more UI tasks) */
				onComplete?: (response: any) => void;
				/** Called when workflow errors - receives structured error */
				onError?: (error: WorkflowError) => void;
				/** Called on timeout - opportunity to show graceful fallback */
				onTimeout?: (workflowId: string, elapsed: number) => void;
				/** Custom fallback route if navigation cannot be determined */
				fallbackRoute?: string;
				/** Timeout for polling (ms), default 180000 (3 min) */
				timeout?: number;
				/** Polling interval (ms), default 2000 (2 sec) - uses exponential backoff */
				pollInterval?: number;
			}
		): Promise<WorkflowResumeResult> => {
			const { 
				stateVersion, 
				onProgress, 
				onNavigating,
				onComplete, 
				onError, 
				onTimeout,
				fallbackRoute, 
				timeout = 180000,
				pollInterval: basePollInterval = 2000,
			} = options || {};
			
			const startTime = Date.now();
			let pollCount = 0;
			
			// Helper: Report progress with consistent status messages
			const reportProgress = (percent: number, status: string, detail?: string) => {
				if (onProgress) onProgress(Math.min(100, Math.max(0, percent)), status, detail);
			};
			
			// Helper: Create structured error
			const createError = (code: WorkflowError['code'], message: string, recoverable: boolean, suggestion?: string): WorkflowError => {
				const err = new Error(message) as WorkflowError;
				err.code = code;
				err.recoverable = recoverable;
				err.suggestion = suggestion;
				return err;
			};
			
			// Helper: Track telemetry
			const trackTelemetry = (event: string, data: any) => {
				try {
					fetch(`${base}/api/plugins/telemetry`, {
						method: 'POST',
						credentials: 'include',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ 
							event: `workflow.${event}`, 
							...data,
							pluginId,
							taskId,
							duration: Date.now() - startTime,
						})
					}).catch(() => {});
				} catch {}
			};
			
			try {
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 1: Resume Task (0-20%)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				reportProgress(5, 'Submitting...', 'Sending your response');
				
				const response = await workflow.resumeUiTask(taskId, data, stateVersion);
				
				reportProgress(20, 'Processing...', 'Workflow is executing');
				trackTelemetry('resumed', { status: response?.status });
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 2: Extract workflow state (20-30%)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				const newTask = response?.workflow?.current_task ||
				                response?.result?.workflow?.current_task ||
				                response?.results?.workflow?.current_task;
				const workflowId = response?.workflow?.id ||
				                   response?.result?.workflow?.id ||
				                   response?.workflow_id;
				const workflowStatus = response?.status ||
				                       response?.result?.workflow_status ||
				                       response?.results?.workflow_status;
				const intelligence = response?.workflow?.intelligence ||
				                     response?.result?.workflow?.intelligence;
				
				// Use workflow intelligence for progress if available
				if (intelligence?.progress) {
					const progressPercent = parseInt(intelligence.progress, 10) || 30;
					reportProgress(progressPercent, 'Processing...', `${intelligence.completed_nodes || 0}/${intelligence.total_nodes || '?'} steps complete`);
				} else {
					reportProgress(30, 'Processing...', 'Workflow is running');
				}
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 3: Handle completion/error states (30-40%)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				if (workflowStatus === 'COMPLETED' || workflowStatus === 'completed') {
					reportProgress(100, 'Complete!', 'Workflow finished successfully');
					trackTelemetry('completed', { duration: Date.now() - startTime });
					if (onComplete) onComplete(response);
					if (fallbackRoute) {
						setTimeout(() => navigateToRoute(fallbackRoute), 300); // Brief pause for UX
					}
					return response;
				}
				
				if (workflowStatus === 'ERROR' || workflowStatus === 'FAILED') {
					const error = createError(
						'WORKFLOW_FAILED',
						response?.message || 'Workflow execution failed',
						true,
						'Please try again or contact support if the problem persists.'
					);
					trackTelemetry('failed', { error: error.message });
					if (onError) onError(error);
					throw error;
				}
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 4: Immediate navigation if new task available (40-50%)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				if (newTask && newTask.id && newTask.id !== taskId) {
					reportProgress(50, 'Preparing next step...', `Moving to ${newTask.name || 'next task'}`);
					
					const taskDetails = await workflow.getTask(newTask.id);
					const contract = taskDetails?.ui_contract;
					
					if (contract) {
						const resolver = customRouteResolver || defaultRouteResolver;
						const route = resolver(contract, newTask.id);
						
						if (route) {
							reportProgress(90, 'Navigating...', 'Almost there');
							trackTelemetry('navigating', { route, taskName: newTask.name });
							if (onNavigating) onNavigating(newTask, route);
							navigateToRoute(route);
							return response;
						}
					}
					
					// Contract-based navigation failed
					if (fallbackRoute) {
						reportProgress(90, 'Navigating...', 'Using fallback route');
						trackTelemetry('fallback_navigation', { fallbackRoute });
						navigateToRoute(fallbackRoute);
						return response;
					}
				}
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 5: Polling with exponential backoff (50-90%)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				if (workflowId && (workflowStatus === 'WAITING_FOR_INPUT' || workflowStatus === 'RUNNING')) {
					reportProgress(50, 'Processing...', 'This may take a moment');
					
					const poll = async (): Promise<any> => {
						pollCount++;
						const elapsed = Date.now() - startTime;
						
						// Timeout check with graceful callback
						if (elapsed > timeout) {
							reportProgress(85, 'Taking longer than expected...', 'You can check back later');
							trackTelemetry('timeout', { elapsed, pollCount });
							if (onTimeout) {
								onTimeout(workflowId, elapsed);
								return response; // Let caller handle timeout gracefully
							}
							if (fallbackRoute) navigateToRoute(fallbackRoute);
							return response;
						}
						
						// Exponential backoff: 2s, 3s, 4.5s, 6.75s, max 10s
						const currentInterval = Math.min(basePollInterval * Math.pow(1.5, pollCount - 1), 10000);
						await new Promise(resolve => setTimeout(resolve, currentInterval));
						
						// Progress: 50% → 85% over timeout period
						const progressPercent = 50 + (35 * Math.min(elapsed / timeout, 1));
						reportProgress(progressPercent, 'Processing...', `Checking status (attempt ${pollCount})`);
						
						const status = await workflow.getWorkflowStatus(workflowId);
						const currentTask = status?.result?.workflow?.current_task ||
						                    status?.workflow?.current_task;
						const currentStatus = status?.status || status?.result?.workflow_status;
						const currentIntelligence = status?.result?.workflow?.intelligence;
						
						// Update progress from workflow intelligence
						if (currentIntelligence?.progress) {
							const wfProgress = parseInt(currentIntelligence.progress, 10);
							if (wfProgress > 0) {
								reportProgress(Math.max(progressPercent, wfProgress), 'Processing...', 
									`${currentIntelligence.completed_nodes || 0}/${currentIntelligence.total_nodes || '?'} steps complete`);
							}
						}
						
						// Check completion
						if (currentStatus === 'COMPLETED' || currentStatus === 'completed') {
							reportProgress(100, 'Complete!', 'Workflow finished successfully');
							trackTelemetry('completed', { duration: elapsed, pollCount });
							if (onComplete) onComplete(status);
							if (fallbackRoute) {
								setTimeout(() => navigateToRoute(fallbackRoute), 300);
							}
							return status;
						}
						
						// Check error
						if (currentStatus === 'ERROR' || currentStatus === 'FAILED') {
							const error = createError(
								'WORKFLOW_FAILED',
								status?.message || 'Workflow execution failed',
								true,
								'Please try again or contact support.'
							);
							trackTelemetry('failed', { error: error.message, pollCount });
							if (onError) onError(error);
							throw error;
						}
						
						// Check for new task
						if (currentTask && currentTask.id !== taskId) {
							reportProgress(88, 'Preparing next step...', `Moving to ${currentTask.name || 'next task'}`);
							
							const taskDetails = await workflow.getTask(currentTask.id);
							const contract = taskDetails?.ui_contract;
							
							if (contract) {
								const resolver = customRouteResolver || defaultRouteResolver;
								const route = resolver(contract, currentTask.id);
								
								if (route) {
									reportProgress(95, 'Navigating...', 'Almost there');
									trackTelemetry('navigating', { route, taskName: currentTask.name, pollCount });
									if (onNavigating) onNavigating(currentTask, route);
									navigateToRoute(route);
									return status;
								}
							}
							
							// Couldn't determine route - use fallback
							if (fallbackRoute) {
								reportProgress(95, 'Navigating...', 'Using fallback');
								navigateToRoute(fallbackRoute);
								return status;
							}
						}
						
						// Keep polling
						return poll();
					};
					
					return poll();
				}
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 6: Graceful fallback (status unclear)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				reportProgress(100, 'Done', 'Response submitted');
				if (fallbackRoute) {
					setTimeout(() => navigateToRoute(fallbackRoute), 500);
				}
				return response;
				
			} catch (error: any) {
				// Structured error handling
				const workflowError: WorkflowError = error.code ? error : createError(
					'RESUME_FAILED',
					error?.message || 'Failed to resume workflow',
					error?.message?.includes('state_version') || error?.message?.includes('concurrency'),
					error?.message?.includes('state_version') 
						? 'The task was modified. Please refresh and try again.'
						: 'Please try again or contact support.'
				);
				
				trackTelemetry('error', { 
					code: workflowError.code, 
					message: workflowError.message,
					recoverable: workflowError.recoverable 
				});
				
				if (onError) onError(workflowError);
				throw workflowError;
			}
		},
		
		/**
		 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
		 * 🚀 START AND CONTINUE - Blue Steel Level Workflow Kickoff
		 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
		 * 
		 * The ONE method to start workflows with Rich UI. Handles everything:
		 * ✅ Starts workflow via CRUD API
		 * ✅ Extracts task_id from 7+ possible response locations
		 * ✅ Auto-navigates to first UI step using route resolver
		 * ✅ Progress callbacks (0-100%)
		 * ✅ Graceful error handling with recovery guidance
		 * ✅ Telemetry for observability
		 * ✅ Full TypeScript types
		 * 
		 * Blue Steel Score: 95% (Zero Friction + Intelligent Automation)
		 * 
		 * @example
		 * // 🎯 Minimal - SDK handles EVERYTHING (200+ lines → 1 line):
		 * await sdk.workflow.startAndContinue('create_complete_agent', { creator_id: userId });
		 * 
		 * @example
		 * // 🎯 With progress tracking for beautiful loading UX:
		 * await sdk.workflow.startAndContinue('delete_agent_cascade_guided', {
		 *   agent_id: agentId,
		 *   deleted_by: userId,
		 * }, {
		 *   onProgress: (pct, status) => setLoading({ percent: pct, message: status }),
		 *   onNavigating: (task, route) => console.log('Navigating to:', route),
		 *   onComplete: () => toast.success('Workflow completed!'),
		 *   fallbackRoute: '/agents',
		 * });
		 */
		startAndContinue: async (
			workflowName: string,
			data?: Record<string, any>,
			options?: {
				onProgress?: (percent: number, status: string, detail?: string) => void;
				onNavigating?: (task: any, route: string) => void;
				onComplete?: (response: WorkflowStartResult) => void;
				onError?: (error: WorkflowError) => void;
				fallbackRoute?: string;
				timeout?: number;
			}
		): Promise<WorkflowStartResult> => {
			const {
				onProgress,
				onNavigating,
				onComplete,
				onError,
				fallbackRoute,
				timeout = 30000,
			} = options || {};
			
			const startTime = Date.now();
			
			// Helper: Report progress with consistent status messages
			const reportProgress = (percent: number, status: string, detail?: string) => {
				if (onProgress) onProgress(Math.min(100, Math.max(0, percent)), status, detail);
			};
			
			// Helper: Create structured error
			const createError = (code: WorkflowError['code'], message: string, recoverable: boolean, suggestion?: string): WorkflowError => {
				const err = new Error(message) as WorkflowError;
				err.code = code;
				err.recoverable = recoverable;
				err.suggestion = suggestion;
				return err;
			};
			
			// Helper: Track telemetry
			const trackTelemetry = (event: string, telemetryData: any) => {
				try {
					fetch(`${base}/api/plugins/telemetry`, {
						method: 'POST',
						credentials: 'include',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							event: `workflow.start.${event}`,
							...telemetryData,
							pluginId,
							workflowName,
							duration: Date.now() - startTime,
						})
					}).catch(() => {});
				} catch {}
			};
			
			// Helper: Extract task ID from response (checks 7+ locations)
			const extractTaskId = (response: any): string | null => {
				return response.result?.workflow?.current_task?.id ||    // ✅ Primary: Nested in result.workflow
				       response.workflow?.current_task?.id ||            // Alternative: Top-level workflow
				       response.result?.current_task?.id ||              // Result level direct
				       response.current_task?.id ||                      // Top-level direct
				       response.task_id ||                               // Top-level fallback
				       response.result?.task_id ||                       // Result level fallback
				       response.result?.request_format?.required_structure?.task_id || // From request_format
				       null;
			};
			
			// Helper: Extract workflow ID from response
			const extractWorkflowId = (response: any): string | null => {
				return response.result?.workflow?.id ||
				       response.workflow?.id ||
				       response.workflow_id ||
				       response.result?.workflow_id ||
				       null;
			};
			
			try {
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 1: Start Workflow (0-30%)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				reportProgress(5, 'Starting workflow...', `Initializing ${workflowName}`);
				
				const response = await crud.executeWorkflow(workflowName, data || {});
				
				reportProgress(30, 'Processing...', 'Workflow started');
				trackTelemetry('started', { status: response?.status });
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 2: Handle Error States (30-40%)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				const workflowStatus = response?.status;
				
				// Check for immediate failure
				if (workflowStatus === 'COMPLETED' && response.message?.includes('failed')) {
					const error = createError(
						'WORKFLOW_FAILED',
						response.message || 'Workflow failed immediately',
						false,
						'The workflow could not complete. Please check the inputs and try again.'
					);
					trackTelemetry('failed', { error: error.message });
					if (onError) onError(error);
					throw error;
				}
				
				if (workflowStatus === 'ERROR' || workflowStatus === 'FAILED') {
					const error = createError(
						'WORKFLOW_FAILED',
						response.message || response.error || 'Workflow execution failed',
						true,
						'Please try again or contact support if the problem persists.'
					);
					trackTelemetry('failed', { error: error.message });
					if (onError) onError(error);
					throw error;
				}
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 3: Handle Completion (Rare - no UI steps)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				if (workflowStatus === 'COMPLETED' && !response.message?.includes('failed')) {
					reportProgress(100, 'Complete!', 'Workflow finished');
					trackTelemetry('completed', { duration: Date.now() - startTime });
					
					const result: WorkflowStartResult = {
						status: 'COMPLETED',
						message: response.message,
						workflowId: extractWorkflowId(response) ?? undefined,
						result: response.result,
						correlation_id: response.correlation_id,
					};
					
					if (onComplete) onComplete(result);
					if (fallbackRoute) {
						setTimeout(() => navigateToRoute(fallbackRoute), 300);
					}
					return result;
				}
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 4: Handle Waiting for UI Input (Expected Case)
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				if (workflowStatus === 'WAITING_FOR_INPUT' || response.message?.includes('paused for experience_ui')) {
					reportProgress(50, 'Preparing UI...', 'Workflow waiting for input');
					
					const taskId = extractTaskId(response);
					const workflowId = extractWorkflowId(response) ?? undefined;
					
					if (taskId) {
						// Fetch task details to get ui_contract for route resolution
						reportProgress(60, 'Loading step...', 'Fetching task details');
						
						try {
							const taskDetails = await workflow.getTask(taskId);
							const contract = taskDetails?.ui_contract;
							
							if (contract) {
								reportProgress(80, 'Navigating...', 'Resolving route');
								
								const resolver = customRouteResolver || defaultRouteResolver;
								const route = resolver(contract, taskId);
								
								if (route) {
									reportProgress(95, 'Navigating...', 'Opening workflow step');
									trackTelemetry('navigating', { route, handlerId: contract.handler_id });
									
									const currentTask = response.result?.workflow?.current_task || 
									                    response.workflow?.current_task ||
									                    { id: taskId, name: workflowName };
									
									if (onNavigating) onNavigating(currentTask, route);
									navigateToRoute(route);
									
									return {
										status: 'WAITING_FOR_INPUT',
										message: response.message,
										taskId,
										workflowId,
										result: response.result,
										correlation_id: response.correlation_id,
										navigatedTo: route,
									};
								}
							}
							
							// Contract exists but route couldn't be resolved - use fallback
							console.warn('[SDK] Route resolution failed for contract:', contract);
						} catch (taskError) {
							// Task fetch failed - try fallback navigation
							console.warn('[SDK] Failed to fetch task details:', taskError);
						}
						
						// Fallback: Navigate using fallbackRoute if provided
						if (fallbackRoute) {
							reportProgress(90, 'Navigating...', 'Using fallback route');
							trackTelemetry('fallback_navigation', { fallbackRoute, taskId });
							navigateToRoute(fallbackRoute);
							
							return {
								status: 'WAITING_FOR_INPUT',
								message: response.message,
								taskId,
								workflowId,
								result: response.result,
								correlation_id: response.correlation_id,
								navigatedTo: fallbackRoute,
							};
						}
						
						// No fallback - return taskId for manual handling
						reportProgress(100, 'Ready', 'Workflow started');
						console.warn('[SDK] No route resolved and no fallback. TaskId:', taskId);
						
						return {
							status: 'WAITING_FOR_INPUT',
							message: response.message,
							taskId,
							workflowId,
							result: response.result,
							correlation_id: response.correlation_id,
						};
					}
					
					// No task ID found - this shouldn't happen
					console.error('[SDK] WAITING_FOR_INPUT but no task_id found. Response:', {
						status: response.status,
						hasResult: !!response.result,
						hasWorkflow: !!response.result?.workflow,
						hasCurrentTask: !!response.result?.workflow?.current_task,
						paths: {
							'result.workflow.current_task.id': response.result?.workflow?.current_task?.id,
							'workflow.current_task.id': response.workflow?.current_task?.id,
							'task_id': response.task_id,
						},
					});
					
					const error = createError(
						'WORKFLOW_FAILED',
						'Workflow paused for UI but no task_id returned',
						true,
						'Please try again. If the problem persists, contact support.'
					);
					trackTelemetry('no_task_id', { response: JSON.stringify(response).slice(0, 500) });
					if (onError) onError(error);
					throw error;
				}
				
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				// PHASE 5: Unknown Status - Return what we have
				// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
				reportProgress(100, 'Started', 'Workflow in progress');
				console.warn('[SDK] Unknown workflow status:', workflowStatus, response);
				
				const taskId = extractTaskId(response) ?? undefined;
				const workflowId = extractWorkflowId(response) ?? undefined;
				
				return {
					status: workflowStatus || 'UNKNOWN',
					message: response.message,
					taskId,
					workflowId,
					result: response.result,
					correlation_id: response.correlation_id,
				};
				
			} catch (error: any) {
				// Handle errors that weren't caught above
				if (error.code) {
					// Already a WorkflowError - re-throw
					throw error;
				}
				
				const workflowError = createError(
					'NETWORK_ERROR',
					error?.message || 'Failed to start workflow',
					true,
					'Please check your connection and try again.'
				);
				
				trackTelemetry('error', {
					code: workflowError.code,
					message: workflowError.message,
				});
				
				if (onError) onError(workflowError);
				throw workflowError;
			}
		},
		
		/**
		 * Configure a custom route resolver for this plugin
		 * Use this if the default convention doesn't work for your routes
		 * 
		 * @example
		 * sdk.workflow.setRouteResolver((contract, taskId) => {
		 *   const routes = {
		 *     'BulkDeletionResults': '/agents/bulk-delete-results',
		 *   };
		 *   const base = routes[contract.handler_id];
		 *   return base ? `${base}/${taskId}` : null;
		 * });
		 */
		setRouteResolver: (resolver: (contract: any, taskId: string) => string | null): void => {
			customRouteResolver = resolver;
		},
	};

	// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
	// I18N SDK - Plugin-scoped translations
	// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
	
	// Access global i18n state exposed by LocalizationProvider
	const getGlobalI18n = () => (window as any).__EXPERIENCE_I18N__ || {
		locale: 'en-US',
		resolvedLocale: 'en-US',
		direction: 'ltr',
		shellCatalog: {},
		pluginCatalogs: new Map(),
	};
	
	const i18n = {
		get locale() { return getGlobalI18n().locale; },
		get resolvedLocale() { return getGlobalI18n().resolvedLocale; },
		get direction() { return getGlobalI18n().direction; },
		
		/**
		 * Translate a key with optional interpolation
		 * Auto-prefixes with 'plugins.{pluginId}.' if not already prefixed
		 */
		t: (key: string, params?: Record<string, unknown>): string => {
			const global = getGlobalI18n();
			
			// Determine the full key
			const fullKey = key.startsWith('shell.') || key.startsWith('plugins.')
				? key
				: `plugins.${pluginId}.${key}`;
			
			// Try plugin catalog first
			const pluginCatalog = global.pluginCatalogs?.get(pluginId) || {};
			let message = pluginCatalog[fullKey] || pluginCatalog[key];
			
			// Fallback to shell catalog
			if (!message) {
				message = global.shellCatalog?.[fullKey] || global.shellCatalog?.[key];
			}
			
			// No translation found - return key for dev visibility
			if (!message) {
				if (import.meta.env.DEV) {
					console.warn(`[i18n:${pluginId}] Missing translation: ${fullKey}`);
				}
				return key;
			}
			
			// Simple parameter replacement (no ICU in SDK - keep it lightweight)
			if (params) {
				Object.entries(params).forEach(([k, v]) => {
					message = message!.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
				});
			}
			
			return message;
		},
		
		formatDate: (date: Date | number, options?: Intl.DateTimeFormatOptions): string => {
			try {
				return new Intl.DateTimeFormat(getGlobalI18n().resolvedLocale, options).format(date);
			} catch {
				return String(date);
			}
		},
		
		formatNumber: (num: number, options?: Intl.NumberFormatOptions): string => {
			try {
				return new Intl.NumberFormat(getGlobalI18n().resolvedLocale, options).format(num);
			} catch {
				return String(num);
			}
		},
		
		formatCurrency: (amount: number, currency = 'USD'): string => {
			try {
				return new Intl.NumberFormat(getGlobalI18n().resolvedLocale, {
					style: 'currency',
					currency,
				}).format(amount);
			} catch {
				return `${currency} ${amount.toFixed(2)}`;
			}
		},
		
		formatRelativeTime: (value: number, unit: Intl.RelativeTimeFormatUnit): string => {
			try {
				return new Intl.RelativeTimeFormat(getGlobalI18n().resolvedLocale, {
					numeric: 'auto',
				}).format(value, unit);
			} catch {
				return `${value} ${unit}`;
			}
		},
	};

	const safeJson = (r: Response) => {
		if (r.status === 204) return undefined;
		const ct = r.headers.get('content-type') || '';
		return ct.includes('application/json') ? r.json() : undefined;
	};

	return {
		api: {
			fetch: apiFetch,
			get: (path: string, config?: { signal?: AbortSignal }) => apiGetJson(path, config),
			post: (path: string, body?: unknown) => apiFetch(path, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: body != null ? JSON.stringify(body) : undefined,
			}).then(safeJson),
			patch: (path: string, body?: unknown) => apiFetch(path, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: body != null ? JSON.stringify(body) : undefined,
			}).then(safeJson),
		delete: (path: string) => apiFetch(path, { method: 'DELETE' }).then(safeJson),
		useQuery: (key: any[], fn: () => Promise<any>, opts?: any) => ReactQuery.useQuery({ queryKey: key, queryFn: fn, ...opts })
		},
		sse: { subscribe, unsubscribeAll: () => { activeSubscriptions.splice(0).forEach(fn => fn()); } },
		authz: {
			normalize: {
				resource: normalizeAuthZENResource,
				action: normalizeAuthZENAction,
			},
			/**
			 * Evaluate a single authorization request
			 * Automatically injects plugin-specific pdp_application and base context
			 */
			evaluate: async (req: any) => {
				const body: any = { ...(req || {}) };
				
				// Auto-populate subject from session if not provided
				if (!body.subject) {
					try {
						body.subject = await getAuthzenSubject();
					} catch (err) {
						// FAIL CLOSED NOISILY: Identity governance requires known subject for all authz checks
						console.error(`[IGA:AUTHZ] sdk.authz.evaluate(${pluginId}): Subject resolution failed — failing closed.`, err);
						return { decision: false };
					}
				}

				// Validate + normalize request shape before calling the PDP (fail closed).
				try {
					body.resource = normalizeAuthZENResource(body.resource);
					body.action = normalizeAuthZENAction(body.resource.type, body.action);
				} catch (err) {
					console.error(
						`[IGA:AUTHZ] sdk.authz.evaluate(${pluginId}): Invalid AuthZEN request — failing closed.`,
						err,
						{ resource: body.resource, action: body.action }
					);
					return { decision: false };
				}
				
				// Inject plugin-specific context (base + request-specific)
				body.context = {
					...baseContext,                                           // From manifest
					...(body.context || {}),                                  // Request-specific
					pdp_application: body.context?.pdp_application || pdpApplication,  // Plugin scope
					decision_intent: body.context?.decision_intent || 'interactive_user_action',
					decision_surface: body.context?.decision_surface || 'plugin_action',
					decision_origin: body.context?.decision_origin || 'experience_plugin_sdk',
					decision_is_background:
						typeof body.context?.decision_is_background === 'boolean'
							? body.context.decision_is_background
							: false,
				};
				
				// Also add pdp_application to resource properties for policy evaluation
				body.resource.properties = {
					...(body.resource.properties || {}),
					pdp_application: body.resource?.properties?.pdp_application || pdpApplication
				};
				
				return apiFetch('/access/v1/evaluation', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(body)
				}).then(r => r.json());
			},
			
			/**
			 * Evaluate multiple authorization requests in a single batch
			 * Automatically injects plugin-specific pdp_application and base context
			 */
			batchEvaluate: async (evaluations: any[]) => {
				const evs = Array.isArray(evaluations) ? evaluations.slice() : [];
				let subject: any = undefined;
				try {
					subject = await getAuthzenSubject();
				} catch (err) {
					// FAIL CLOSED NOISILY: Identity governance requires known subject for all authz checks
					console.error(`[IGA:AUTHZ] sdk.authz.batchEvaluate(${pluginId}): Subject resolution failed — failing closed.`, err);
					return { evaluations: evs.map(() => ({ decision: false })) };
				}

				// Validate + normalize before calling the PDP (avoid rejecting the whole batch).
				let payload: any[] = [];
				try {
					payload = evs.map(e => {
						const resource = normalizeAuthZENResource(e.resource);
						const action = normalizeAuthZENAction(resource.type, e.action);

						// Inject plugin-specific context into each evaluation
						const context = {
							...baseContext,                                        // From manifest
							...(e.context || {}),                                  // Request-specific
							pdp_application: e?.context?.pdp_application || pdpApplication,  // Plugin scope
							decision_intent: e?.context?.decision_intent || 'interactive_user_action',
							decision_surface: e?.context?.decision_surface || 'plugin_action',
							decision_origin: e?.context?.decision_origin || 'experience_plugin_sdk',
							decision_is_background:
								typeof e?.context?.decision_is_background === 'boolean'
									? e.context.decision_is_background
									: false,
						};

						resource.properties = {
							...(resource.properties || {}),
							pdp_application: resource.properties?.pdp_application || pdpApplication
						};

						return { subject, resource, action, context };
					});
				} catch (err) {
					console.error(
						`[IGA:AUTHZ] sdk.authz.batchEvaluate(${pluginId}): Invalid AuthZEN batch request — failing closed.`,
						err,
					);
					return { evaluations: evs.map(() => ({ decision: false })) };
				}
				
				return apiFetch('/access/v1/evaluations', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ evaluations: payload })
				}).then(r => r.json());
			}
		},
		navigate: navigateToRoute,
		crud,
		workflow,
		
		/**
		 * Membership SDK - Access membership service data (identities, agents, groups)
		 * @example const identity = await sdk.membership.getIdentity('auth:account:empowernow:test');
		 */
		membership: createMembershipSdk({
			fetch: apiFetch,
			get: (path: string, config?: { signal?: AbortSignal }) => apiGetJson(path, config),
			post: (path: string, body?: unknown) => apiFetch(path, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: body != null ? JSON.stringify(body) : undefined,
			}).then(safeJson),
			patch: (path: string, body?: unknown) => apiFetch(path, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: body != null ? JSON.stringify(body) : undefined,
			}).then(safeJson),
		delete: (path: string) => apiFetch(path, { method: 'DELETE' }).then(safeJson),
	}),
		
		logger: console,
		telemetry: { track: (_e: string, _p?: any) => void 0 },
		
		/**
		 * Plugin metadata - useful for debugging and telemetry
		 */
		metadata: {
			pluginId,
			pdpApplication,
			baseContext: Object.keys(baseContext).length > 0 ? baseContext : undefined,
			config: Object.keys(pluginConfig).length > 0 ? pluginConfig : undefined
		},
		
		/**
		 * Plugin-specific configuration (shorthand for metadata.config)
		 * @since 1.3.0
		 */
		config: Object.keys(pluginConfig).length > 0 ? pluginConfig : undefined,
		
		/**
		 * Internationalization utilities
		 * @since 1.4.0
		 */
		i18n,

		/**
		 * Connector system discovery and capabilities.
		 * Opt-in via manifest config.systemDiscovery.
		 *
		 * - discover(): returns the SearchPEP-filtered system list
		 * - capabilities(systemName): per-system action flags via batch evaluate
		 * @since 2.0.0
		 */
		systems: {
			discover: async (opts?: { signal?: AbortSignal }): Promise<any[]> => {
				const disc = pluginConfig?.systemDiscovery;
				if (disc?.type) {
					const params = new URLSearchParams();
					params.set('type', disc.type);
					if (disc.provider) params.set('provider', disc.provider);
					const data: any = await apiGetJson(`/api/crud/configs/systems/summary?${params}`, opts);
					return data?.items ?? [];
				}
				if (disc?.roles) {
					const seen = new Set<string>();
					const allItems: any[] = [];
					for (const role of Object.values(disc.roles) as any[]) {
						const params = new URLSearchParams();
						if (role.type) params.set('type', role.type);
						if (role.provider) params.set('provider', role.provider);
						const data: any = await apiGetJson(`/api/crud/configs/systems/summary?${params}`, opts);
						for (const item of data?.items ?? []) {
							if (!seen.has(item.name)) { seen.add(item.name); allItems.push(item); }
						}
					}
					return allItems;
				}
				const data: any = await apiGetJson('/api/crud/configs/systems/summary', opts);
				return data?.items ?? [];
			},

			capabilities: async (systemName: string): Promise<Record<string, boolean>> => {
				const disc = pluginConfig?.systemDiscovery;
				const systemType = disc?.type;
				if (!systemType) return {};

				const objectTypes = disc?.objectTypes ?? ['user', 'group', 'role'];
				const actions = disc?.actions ?? ['list', 'create', 'read', 'update', 'delete'];
				const evaluations = objectTypes.flatMap((ot: string) =>
					actions.map((act: string) => ({
						resource: { type: 'connector_system', id: systemName, properties: { system: systemName } },
						action: { name: `op:${systemType}:${ot}.${act}` },
						context: { connector_system_id: systemName },
					}))
				);

				try {
					const result: any = await apiFetch('/access/v1/evaluations', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ evaluations: evaluations.map((e: any) => ({
							...e,
							subject: undefined,
						})) }),
					}).then(r => r.json());

					const caps: Record<string, boolean> = {};
					const evResults = result?.evaluations ?? [];
					let idx = 0;
					for (const ot of objectTypes) {
						for (const act of actions) {
							caps[`${ot}.${act}`] = evResults[idx]?.decision === true;
							idx++;
						}
					}
					return caps;
				} catch {
					return {};
				}
			},
		},
	};
}


